import React, { useEffect, useState } from 'react';
import deburr from 'lodash/deburr';

import useApi, { ApiProvider } from 'api';
import CreateCompany from 'onboarding/CreateCompany';
import authRoutes from 'routes/codegen/authRoutes';
import { useAsyncFunction, useHistory } from 'stores/hooks';
import { Button, Divider, Input, Loader } from 'ui';

const normalize = (value: string) => deburr(value.normalize('NFD')).toLowerCase();

function Launchpad() {
  const api = useApi();

  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [data, setData] = useState<Backend.Launchpad | null>(null);
  const [createCompany, setCreateCompany] = useState(false);
  const [showDisabled, setShowDisabled] = useState(false);
  const [search, setSearch] = useState('');
  const history = useHistory();

  async function load() {
    const response = await api.LaunchpadApi.retrieve();
    setData(response);
  }

  const [handleLoad, isLoading] = useAsyncFunction(load);

  useEffect(() => {
    handleLoad();
  }, []);

  const handleOnCreate = (companyId: UUIDField) => {
    setIsCreating(true);
    window.location.replace(`/company/set-active-account/${companyId}/`);
  };

  const getCompanyUrl = (companyId: UUIDField) => {
    const baseUrl = authRoutes.companySetActiveAccountCompanyDetail({
      companyId,
      lang: window.appState.lang,
    });
    const nextUrl = new URLSearchParams(history.location.search).get('next');

    if (!nextUrl) return baseUrl;
    return `${baseUrl}?next=${nextUrl}`;
  };

  if (isLoading || isCreating || data === null) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <div>
          <Loader size="huge" />
        </div>
      </div>
    );
  }

  const searchTerm = normalize(search.trim());

  const filterVisibleCompanies = (companies: Backend.SimpleCompany[]) =>
    companies.filter(
      (company) => (showDisabled || !company.is_disabled) && normalize(company.name).includes(searchTerm),
    );

  const hasDisabled = [...data.member_accounts, ...data.also_available].some((company) => company.is_disabled);

  const memberAccounts = filterVisibleCompanies(data.member_accounts);
  const alsoAvailable = filterVisibleCompanies(data.also_available);

  const renderCompanyButton = (company: Backend.SimpleCompany) => (
    <div key={company.id}>
      <a href={getCompanyUrl(company.id)}>
        <Button fluid wrap grayLink={company.is_disabled}>
          <span className="min-w-0 break-words">{company.name}</span>
        </Button>
      </a>
    </div>
  );

  return (
    <>
      <h2 className="text-center text-xl font-medium text-gray-900">{gettext('Switch company')}</h2>
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="space-y-4">
            {data.also_available.length > 0 && (
              <Input
                type="search"
                icon="search"
                autoFocus
                autoComplete="off"
                placeholder={`${gettext('Search')}...`}
                value={search}
                onChange={setSearch}
              />
            )}
            {memberAccounts.map(renderCompanyButton)}
            {hasDisabled && (
              <div>
                <Button fluid link onClick={() => setShowDisabled((value) => !value)}>
                  {showDisabled ? gettext('Hide deactivated accounts') : gettext('Show deactivated accounts')}
                </Button>
              </div>
            )}
            {createCompany ? (
              <CreateCompany onCreate={handleOnCreate} />
            ) : (
              <>
                <div>
                  <Button fluid link onClick={() => setCreateCompany(true)}>
                    {gettext('Create new company')}
                  </Button>
                </div>
                <div>
                  <a href={authRoutes.authLogout({ lang: window.appState.lang })}>
                    <Button fluid link>
                      {gettext('Log out')}
                    </Button>
                  </a>
                </div>
              </>
            )}
            {alsoAvailable.length > 0 && (
              <>
                <Divider />
                {alsoAvailable.map(renderCompanyButton)}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function LaunchpadWrapper() {
  // User is logged in, so we can provide the private API context
  return (
    <ApiProvider>
      <Launchpad />
    </ApiProvider>
  );
}

export default LaunchpadWrapper;
